import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DollarSign, TrendingUp, BarChart3, Mail, Eye, Heart,
  Package, ShoppingBag, Loader2, Zap, UserCheck, AlertTriangle, UserX
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EmailHealthWidget({ darkMode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardBg = darkMode ? "bg-gray-800/80 border border-gray-700/50" : "";

  useEffect(() => {
    const fetchScores = async () => {
      try {
        const token = localStorage.getItem("gmp_token");
        const res = await axios.get(`${API}/admin/email-analytics/engagement-scores`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = res.data || {};
        const segs = result.segments || {};
        const total = result.total_scored || 0;
        setData({
          engaged: segs.engaged || 0,
          atRisk: segs.at_risk || 0,
          inactive: segs.inactive || 0,
          total,
        });
      } catch {
        setData(null);
      } finally { setLoading(false); }
    };
    fetchScores();
  }, []);

  if (loading) return null;
  if (!data || data.total === 0) return null;

  const pctEngaged = Math.round((data.engaged / data.total) * 100);
  const pctAtRisk = Math.round((data.atRisk / data.total) * 100);
  const pctInactive = Math.round((data.inactive / data.total) * 100);

  const segments = [
    { label: "Engaged", count: data.engaged, pct: pctEngaged, iconBg: "bg-emerald-500/15", iconColor: "text-emerald-500", icon: UserCheck },
    { label: "At Risk", count: data.atRisk, pct: pctAtRisk, iconBg: "bg-amber-500/15", iconColor: "text-amber-500", icon: AlertTriangle },
    { label: "Inactive", count: data.inactive, pct: pctInactive, iconBg: "bg-red-500/15", iconColor: "text-red-500", icon: UserX },
  ];

  return (
    <Card className={`border-0 shadow-soft rounded-2xl ${cardBg}`} data-testid="email-health-widget">
      <CardHeader className="pb-2">
        <CardTitle className={`text-base flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}>
          <Zap className="w-4 h-4 text-amber-500" /> Email Engagement Health
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Progress bar */}
        <div className={`h-3 rounded-full overflow-hidden flex mb-4 ${darkMode ? "bg-gray-700" : "bg-gray-100"}`} data-testid="health-progress-bar">
          {pctEngaged > 0 && <div className="bg-emerald-500 transition-all" style={{ width: `${pctEngaged}%` }} />}
          {pctAtRisk > 0 && <div className="bg-amber-400 transition-all" style={{ width: `${pctAtRisk}%` }} />}
          {pctInactive > 0 && <div className="bg-red-400 transition-all" style={{ width: `${pctInactive}%` }} />}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {segments.map(({ label, count, pct, iconBg, iconColor, icon: Icon }) => (
            <div key={label} className={`text-center p-3 rounded-xl ${darkMode ? "bg-gray-700/50" : "bg-gray-50"}`} data-testid={`health-segment-${label.toLowerCase().replace(" ", "-")}`}>
              <div className={`w-8 h-8 mx-auto rounded-lg ${iconBg} flex items-center justify-center mb-2`}>
                <Icon className={`w-4 h-4 ${iconColor}`} />
              </div>
              <p className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{pct}%</p>
              <p className={`text-xs font-medium ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{label}</p>
              <p className={`text-[10px] mt-0.5 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>{count} subscriber{count !== 1 ? "s" : ""}</p>
            </div>
          ))}
        </div>
        <p className={`text-[10px] text-center mt-3 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
          Based on {data.total} tracked subscriber{data.total !== 1 ? "s" : ""}
        </p>
      </CardContent>
    </Card>
  );
}

export const OverviewTab = ({ dashboard, darkMode, SalesChart }) => {
  if (!dashboard) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;
  }

  const cardBg = darkMode ? "bg-gray-800/80 border border-gray-700/50" : "";
  const valText = darkMode ? "text-white" : "text-navy-900";
  const lblText = darkMode ? "text-gray-400" : "text-gray-500";
  const subText = darkMode ? "text-gray-500" : "text-gray-400";

  return (
    <div className="space-y-6">
      {/* Revenue Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Today's Revenue", value: `GHS ${dashboard.metrics.today_revenue.toFixed(2)}`, sub: `${dashboard.metrics.today_orders} order(s)`, icon: DollarSign, color: "text-emerald-600 bg-emerald-50", darkColor: "text-emerald-400 bg-emerald-900/30" },
          { label: "This Week", value: `GHS ${dashboard.metrics.week_revenue.toFixed(2)}`, sub: `${dashboard.metrics.week_orders} order(s)`, icon: TrendingUp, color: "text-blue-600 bg-blue-50", darkColor: "text-blue-400 bg-blue-900/30" },
          { label: "This Month", value: `GHS ${dashboard.metrics.month_revenue.toFixed(2)}`, sub: `${dashboard.metrics.month_orders} order(s)`, icon: BarChart3, color: "text-violet-600 bg-violet-50", darkColor: "text-violet-400 bg-violet-900/30" },
          { label: "All-Time Revenue", value: `GHS ${dashboard.metrics.total_revenue.toFixed(2)}`, sub: `${dashboard.metrics.total_products} products`, icon: DollarSign, color: "text-amber-600 bg-amber-50", darkColor: "text-amber-400 bg-amber-900/30" },
        ].map(({ label, value, sub, icon: Icon, color, darkColor }) => {
          const c = darkMode ? darkColor : color;
          return (
            <Card key={label} className={`border-0 shadow-soft rounded-2xl overflow-hidden ${cardBg}`}>
              <CardContent className="p-5">
                <div className={`w-10 h-10 rounded-xl ${c.split(" ")[1]} flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 ${c.split(" ")[0]}`} />
                </div>
                <p className={`text-2xl font-bold ${valText}`}>{value}</p>
                <p className={`text-sm ${lblText}`}>{label}</p>
                <p className={`text-xs mt-1 ${subText}`}>{sub}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Engagement + Subscribers Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Subscribers", value: dashboard.metrics.total_subscribers, sub: `+${dashboard.metrics.week_subscribers} this week`, icon: Mail, color: "text-pink-600 bg-pink-50", darkColor: "text-pink-400 bg-pink-900/30" },
          { label: "Showcase Views", value: dashboard.metrics.total_showcase_views, sub: "all-time", icon: Eye, color: "text-sky-600 bg-sky-50", darkColor: "text-sky-400 bg-sky-900/30" },
          { label: "Showcase Likes", value: dashboard.metrics.total_showcase_likes, sub: "all-time", icon: Heart, color: "text-red-500 bg-red-50", darkColor: "text-red-400 bg-red-900/30" },
          { label: "Products Listed", value: dashboard.metrics.total_products, sub: "in catalog", icon: Package, color: "text-indigo-600 bg-indigo-50", darkColor: "text-indigo-400 bg-indigo-900/30" },
        ].map(({ label, value, sub, icon: Icon, color, darkColor }) => {
          const c = darkMode ? darkColor : color;
          return (
            <Card key={label} className={`border-0 shadow-soft rounded-2xl overflow-hidden ${cardBg}`}>
              <CardContent className="p-5">
                <div className={`w-10 h-10 rounded-xl ${c.split(" ")[1]} flex items-center justify-center mb-3`}>
                  <Icon className={`w-5 h-5 ${c.split(" ")[0]}`} />
                </div>
                <p className={`text-2xl font-bold ${valText}`}>{value}</p>
                <p className={`text-sm ${lblText}`}>{label}</p>
                <p className={`text-xs mt-1 ${subText}`}>{sub}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Sales Chart */}
      <SalesChart data={dashboard.daily_sales || []} darkMode={darkMode} />

      {/* Bottom Row: Top Products + Recent Orders */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className={`border-0 shadow-soft rounded-2xl ${cardBg}`}>
          <CardHeader><CardTitle className={`text-base flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}><TrendingUp className="w-4 h-4 text-emerald-500" /> Top Selling Products</CardTitle></CardHeader>
          <CardContent>
            {dashboard.top_products.length > 0 ? (
              <div className="space-y-3">
                {dashboard.top_products.map((p, i) => (
                  <div key={i} className={`flex items-center justify-between p-2 rounded-xl ${darkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"} transition-colors`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${darkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-500"}`}>{i + 1}</span>
                      <span className={`text-sm font-medium truncate max-w-[180px] ${darkMode ? "text-gray-200" : ""}`}>{p.title}</span>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${darkMode ? "text-white" : ""}`}>GHS {p.revenue.toFixed(2)}</p>
                      <p className={`text-xs ${subText}`}>{p.count} sold</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className={`text-sm text-center py-4 ${subText}`}>No sales yet</p>}
          </CardContent>
        </Card>

        <Card className={`border-0 shadow-soft rounded-2xl ${cardBg}`}>
          <CardHeader><CardTitle className={`text-base flex items-center gap-2 ${darkMode ? "text-gray-100" : ""}`}><ShoppingBag className="w-4 h-4 text-blue-500" /> Recent Orders</CardTitle></CardHeader>
          <CardContent>
            {dashboard.recent_orders.length > 0 ? (
              <div className="space-y-3">
                {dashboard.recent_orders.map((o) => (
                  <div key={o.transaction_ref} className={`flex items-center justify-between p-2 rounded-xl ${darkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"} transition-colors`}>
                    <div>
                      <p className={`text-sm font-medium ${darkMode ? "text-gray-200" : ""}`}>{o.customer_name || o.customer_email}</p>
                      <p className={`text-xs ${subText}`}>{o.created_at ? new Date(o.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${darkMode ? "text-white" : ""}`}>GHS {(o.total_amount || 0).toFixed(2)}</span>
                      <Badge variant={o.status === "completed" ? "default" : "secondary"} className="capitalize rounded-full text-xs">{o.status}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className={`text-sm text-center py-4 ${subText}`}>No orders yet</p>}
          </CardContent>
        </Card>
      </div>

      {/* Email Engagement Health */}
      <EmailHealthWidget darkMode={darkMode} />
    </div>
  );
};
